import {
  lineServerApiDefinition,
  telegramApiDefinition,
} from "../../client/src/apiTyping.ts";

const publicApiDefinition = {
  ...lineServerApiDefinition,
  ...telegramApiDefinition,
} as const;

type ExampleMap = Record<
  keyof typeof publicApiDefinition,
  { request: Record<string, unknown>; response: Record<string, unknown> }
>;

export const endpointExamples: ExampleMap = {
  sendMessage: {
    request: {
      fromNumber: "15551234567",
      toNumber: "15559876543@c.us",
      message: "Hello! This is a test message.",
      linkPreview: false,
      token: "your-secret-token",
    },
    response: { messageId: "3EB0123456789ABCDEF" },
  },
  sendMedia: {
    request: {
      fromNumber: "15551234567",
      toNumber: "15559876543@c.us",
      base64:
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==",
      type: "image",
      caption: "Check out this image!",
      token: "your-secret-token",
    },
    response: { messageId: "3EB0987654321FEDCBA" },
  },
  sendPoll: {
    request: {
      fromNumber: "15551234567",
      toNumber: "120363123456789012@g.us",
      name: "What's your favorite color?",
      choices: ["Red", "Blue", "Green"],
      token: "your-secret-token",
    },
    response: { messageId: "3EB0POLL56789ABCDEF" },
  },
  editMessage: {
    request: {
      fromNumber: "15551234567",
      messageId: "3EB0123456789ABCDEF",
      newText: "Updated message text",
      token: "your-secret-token",
    },
    response: { success: true },
  },
  deleteMessage: {
    request: {
      fromNumber: "15551234567",
      chatId: "15551234567@c.us",
      messageId: "3EB0123456789ABCDEF",
      token: "your-secret-token",
    },
    response: { success: true },
  },
  sendReaction: {
    request: {
      fromNumber: "15551234567",
      messageId: "3EB0123456789ABCDEF",
      reaction: "\u{1F44D}",
      token: "your-secret-token",
    },
    response: { success: true },
  },
  sendTypingIndication: {
    request: {
      fromNumber: "15551234567",
      chatId: "15559876543@c.us",
      durationMs: 3000,
      token: "your-secret-token",
    },
    response: { success: true },
  },
  stopTypingIndication: {
    request: {
      fromNumber: "15551234567",
      chatId: "15559876543@c.us",
      token: "your-secret-token",
    },
    response: { success: true },
  },
  getGroups: {
    request: {
      fromNumber: "15551234567",
      token: "your-secret-token",
    },
    response: {
      groups: [
        { id: "120363123456789012@g.us", name: "My Cool Group", isAdmin: true },
        {
          id: "120363987654321098@g.us",
          name: "Another Group",
          isAdmin: false,
        },
      ],
    },
  },
  addUserToWhatsAppGroup: {
    request: {
      fromNumber: "15551234567",
      groupId: "120363123456789012@g.us",
      userId: "15559876543@c.us",
      token: "your-secret-token",
    },
    response: { success: true },
  },
  removeUserFromWhatsAppGroup: {
    request: {
      fromNumber: "15551234567",
      groupId: "120363123456789012@g.us",
      userId: "15559876543@c.us",
      token: "your-secret-token",
    },
    response: { success: true },
  },
  getCommonWhatsappGroups: {
    request: {
      fromNumber: "15551234567",
      userId: "15559876543@c.us",
      token: "your-secret-token",
    },
    response: {
      groups: [{ id: "120363123456789012@g.us", name: "Shared Group" }],
    },
  },
  getWhatsappJoinRequests: {
    request: {
      fromNumber: "15551234567",
      groupId: "120363123456789012@g.us",
      token: "your-secret-token",
    },
    response: {
      requests: [
        {
          requesterId: "15559876543@c.us",
          addedById: null,
          parentGroupId: null,
          method: "non_admin_add",
          timestamp: 1703251200000,
        },
      ],
    },
  },
  approveWhatsappJoinRequests: {
    request: {
      fromNumber: "15551234567",
      groupId: "120363123456789012@g.us",
      requesterIds: ["15559876543@c.us"],
      token: "your-secret-token",
    },
    response: {
      results: [
        {
          requesterId: "15559876543@c.us",
          error: null,
          message: "Approved successfully",
        },
      ],
    },
  },
  rejectWhatsappJoinRequests: {
    request: {
      fromNumber: "15551234567",
      groupId: "120363123456789012@g.us",
      requesterIds: ["15559876543@c.us"],
      token: "your-secret-token",
    },
    response: {
      results: [
        {
          requesterId: "15559876543@c.us",
          error: null,
          message: "Rejected successfully",
        },
      ],
    },
  },
  getGroupMembers: {
    request: {
      fromNumber: "15551234567",
      groupId: "120363123456789012@g.us",
      token: "your-secret-token",
    },
    response: {
      members: [
        { id: "15559876543@c.us", isAdmin: true, isSuperAdmin: true },
        { id: "15551111111@c.us", isAdmin: false, isSuperAdmin: false },
      ],
    },
  },
  getWhatsappGroupDetails: {
    request: {
      fromNumber: "15551234567",
      groupLink: "https://chat.whatsapp.com/AbCdEfGhIjK",
      token: "your-secret-token",
    },
    response: {
      success: true,
      id: "120363123456789012@g.us",
      subject: "My Group",
      desc: "Group description",
      size: 42,
      participants: [
        { id: "15559876543@c.us", isAdmin: true, isSuperAdmin: false },
      ],
    },
  },
  getGroupInviteLink: {
    request: {
      fromNumber: "15551234567",
      groupId: "120363123456789012@g.us",
      token: "your-secret-token",
    },
    response: {
      inviteLink: null,
    },
  },
  joinGroupByInviteLink: {
    request: {
      fromNumber: "15551234567",
      groupLink: "https://chat.whatsapp.com/AbCdEfGhIjK",
      token: "your-secret-token",
    },
    response: {
      status: "joined",
      chatId: "120363123456789012@g.us",
    },
  },
  blockContact: {
    request: {
      fromNumber: "15551234567",
      contactId: "15559876543@c.us",
      token: "your-secret-token",
    },
    response: { success: true },
  },
  reportContact: {
    request: {
      fromNumber: "15551234567",
      contactId: "15559876543@c.us",
      token: "your-secret-token",
    },
    response: { success: true },
  },
  unblockContact: {
    request: {
      fromNumber: "15551234567",
      contactId: "15559876543@c.us",
      token: "your-secret-token",
    },
    response: { success: true },
  },
  convertLidToNumber: {
    request: {
      fromNumber: "15551234567",
      lid: "119846817792023@lid",
      token: "your-secret-token",
    },
    response: { number: "15559876543@c.us" },
  },
  setWebhook: {
    request: {
      phoneNumber: "15551234567",
      url: "https://your-server.com/webhook",
      token: "your-secret-token",
    },
    response: {},
  },
  addWebhook: {
    request: {
      phoneNumber: "15551234567",
      url: "https://your-server.com/webhook",
      token: "your-secret-token",
    },
    response: {},
  },
  removeWebhook: {
    request: {
      phoneNumber: "15551234567",
      url: "https://your-server.com/webhook",
      token: "your-secret-token",
    },
    response: {},
  },
  reconnect: {
    request: {
      number: "15551234567",
      token: "your-secret-token",
    },
    response: {},
  },
  setLoginMethod: {
    request: {
      phoneNumber: "15551234567",
      preferCodeLogin: true,
      token: "your-secret-token",
    },
    response: { success: true },
  },
  clearCredentials: {
    request: {
      phoneNumber: "15551234567",
      token: "your-secret-token",
    },
    response: { success: true },
  },
  changeWhatsappAccountName: {
    request: {
      phoneNumber: "15551234567",
      name: "My Business Bot",
      token: "your-secret-token",
    },
    response: {},
  },
  changeWhatsappAccountAvatar: {
    request: {
      phoneNumber: "15551234567",
      imageBase64: "/9j/4AAQSkZJRg...",
      token: "your-secret-token",
    },
    response: {},
  },
  changeWhatsappAccountBio: {
    request: {
      phoneNumber: "15551234567",
      bio: "Powered by Supergreen",
      token: "your-secret-token",
    },
    response: {},
  },
  changeWhatsappPrivacySettings: {
    request: {
      phoneNumber: "15551234567",
      category: "about",
      value: "all",
      token: "your-secret-token",
    },
    response: {},
  },
  submitTelegramCode: {
    request: {
      phoneNumber: "15551234567",
      code: "12345",
      token: "your-secret-token",
    },
    response: { success: true },
  },
  sendTelegramMessage: {
    request: {
      phoneNumber: "15551234567",
      chatId: "@example_channel",
      text: "Hello from Supergreen Telegram!",
      token: "your-secret-token",
    },
    response: { success: true, messageId: "12345" },
  },
  getTelegramGroups: {
    request: {
      phoneNumber: "15551234567",
      token: "your-secret-token",
    },
    response: {
      success: true,
      warning:
        "Group list is computed live and may be incomplete for large accounts.",
      cacheMaxAgeSeconds: 0,
      groups: [
        { id: "-1001234567890", name: "My Telegram Group", isAdmin: true },
      ],
    },
  },
  joinTelegramGroup: {
    request: {
      phoneNumber: "15551234567",
      groupUsername: "example_channel",
      token: "your-secret-token",
    },
    response: { success: true, status: "joined", chatId: "example_channel" },
  },
  setTelegramWebhook: {
    request: {
      phoneNumber: "15551234567",
      url: "https://your-server.com/telegram-webhook",
      token: "your-secret-token",
    },
    response: { success: true },
  },
  addTelegramWebhook: {
    request: {
      phoneNumber: "15551234567",
      url: "https://your-server.com/telegram-webhook",
      token: "your-secret-token",
    },
    response: { success: true },
  },
  removeTelegramWebhook: {
    request: {
      phoneNumber: "15551234567",
      url: "https://your-server.com/telegram-webhook",
      token: "your-secret-token",
    },
    response: { success: true },
  },
};
